import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { HealthResponse, Me, Settings } from './types';

export function useHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: async () => (await apiClient.get<HealthResponse>('/api/v1/health')).data,
    staleTime: 30_000
  });
}

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => (await apiClient.get<Me>('/api/v1/auth/me')).data,
    staleTime: 5 * 60_000,
    enabled,
    retry: (count, err) => {
      // Don't retry 401s — let the user sign in.
      const anyErr = err as { response?: { status?: number } };
      if (anyErr?.response?.status === 401) return false;
      return count < 3;
    }
  });
}

export function useUpdateBusinessName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (businessName: string) => {
      await apiClient.patch('/api/v1/auth/me/business-name', { businessName });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] })
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Settings>) => {
      await apiClient.patch('/api/v1/auth/me/settings', patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] })
  });
}
